import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import ModalAbout from "../components/ModalAbout";
import { makeStyles } from "@mui/styles";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import Options from "../components/Options";
import { changeTab } from "../redux/TabSlice";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { getTitleFromAppContent, getTranslatedItemTitle, getContentTitle } from "../helpers/utils";
import { trackPageView } from "../helpers/api";
import ArrowBack from "../assets/img/HumQadamHome/arrowBack.svg";
import { getOffences, setSelectedOffence } from "../redux/OrganizationSlice";
import referralIcon from "../assets/img/HomePage/referral.svg";
import { colors } from "../constants/theme";

const useStyles = makeStyles((_theme) => ({
  referralBg: {
    backgroundColor: colors.primary,
  },
  referralTextBg: {
    backgroundColor: colors.primaryDark,
  },
  // Same intro/description style as EntitiesGrid (Laws, Guidance, Support Providing Orgs)
  introText: {
    fontSize: "0.9375rem",
    color: "#333",
    fontWeight: 400,
    textAlign: "center",
    lineHeight: 1.5,
  },
}));

function ReferralDirectoryEntitiesGrid() {
  const classes = useStyles();
  const { selLanguageId } = useSelector((state) => state.language);
  const { selProvinceId, selDistrictId } = useSelector((state) => ({ selProvinceId: state.province.selProvinceId, selDistrictId: state.district.selDistrictId }));
  const { offences } = useSelector((state) => state.organizations);
  const { appContent } = useSelector((state) => state.appContent);
  const [description, setDescription] = useState("");
  const [openAbout, setOpenAbout] = useState(false);
  const [tabSelect, setTabSelect] = useState("ReferralDirectoryEntitiesGrid");
  const [referralTitle, setReferralTitle] = useState("");
  const dispatch = useDispatch();
  const handleOpen = () => setOpenAbout(true);
  const handleClose = () => {
    setOpenAbout(false);
  };

  useEffect(() => {
    trackPageView("Referral Directory Entities", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [selProvinceId, selDistrictId, selLanguageId]);

  // Fetch offences on mount
  useEffect(() => {
    if (offences.length === 0) {
      dispatch(getOffences({ langId: selLanguageId }));
    }
  }, [dispatch, selLanguageId, offences.length]);

  useEffect(() => {
    dispatch(changeTab(tabSelect));
  }, [tabSelect, dispatch]);

  // Get title and description from app content
  useEffect(() => {
    if (appContent) {
      const title = getContentTitle(appContent, ["Support Providing Organizations", "Referral", "Referral Guide"]);
      setReferralTitle(title);
      // Try to get description, fallback to empty if not available
      const desc = getTitleFromAppContent(appContent, "EntitiesReferralDes");
      setDescription(desc || "");
    }
  }, [appContent]);

  const handleClick = (item) => {
    const itemId = item._id || item.id;
    // Set the selected offence and navigate to ReferralDirectory
    dispatch(setSelectedOffence(itemId));
    dispatch(changeTab("ReferralDirectory"));
  };

  const getOffenceTitle = (offence) => getTranslatedItemTitle(offence, selLanguageId);

  return (
    <div
      className={`w-full pb-10 flex flex-col justify-between`}
      style={{ background: colors.referralBg }}
    >
      <Header />
      <Options handleOpen={handleOpen} />
      {offences?.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="flex flex-col justify-center content-center items-center mb-20">
          <img className="w-12 mb-2" src={referralIcon} alt="Support Organizations" />
          <Typography
            style={{ color: colors.primaryDark }}
            className="text-lg font-bold my-2"
            variant="p"
          >
            {referralTitle}
          </Typography>
          <Typography className={clsx("text-center w-4/6 my-2", classes.introText)} variant="p">
            {description || "Access verified GBV support services in one place. Select the type of violence to view relevant referrals"}
          </Typography>

          <Grid className="p-5" container spacing={2}>
            {offences.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                lg={2}
                key={`${item._id || item.id || 'offence'}-${index}`}
                className="flex justify-center"
              >
                <div
                  onClick={() => {
                    handleClick(item);
                  }}
                  className={clsx(
                    "cursor-pointer py-5 w-full sm:w-36 rounded-3xl"
                  )}
                  style={{ background: colors.primary }}
                >
                  {item.icon && (
                    <img
                      className="h-16 mb-5 mx-auto"
                      src={item.icon}
                      alt={getOffenceTitle(item)}
                    />
                  )}
                  <Typography
                    color={"white"}
                    className="block text-center text-xs"
                    variant="p"
                  >
                    {getOffenceTitle(item)}
                  </Typography>
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      )}
      <img
        alt="Arrow Back"
        onClick={() => setTabSelect("HomePage")}
        src={ArrowBack}
        className="cursor-pointer self-start ml-5"
      />
      <div>
        <ModalAbout open={openAbout} handleClose={handleClose} />
      </div>
    </div>
  );
}

export default ReferralDirectoryEntitiesGrid;
